import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import axios from "axios";
import AdminPage from "@/pages/AdminPage";
import JobsPage from "@/pages/JobsPage";
import OutsourcingPage from "@/pages/OutsourcingPage";
import BlogPage from "@/pages/BlogPage";
import { 
  GraduationCap, 
  Users, 
  TrendingUp, 
  Award,
  BookOpen,
  Briefcase,
  Target,
  Star,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Cpu,
  Brain,
  Zap,
  Cloud,
  Shield,
  Megaphone,
  Server,
  Code,
  CheckCircle,
  Clock,
  IndianRupee
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tech/AI themed stock videos from Pexels (free to use)
const HERO_VIDEOS = [
  "https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4",
  "https://videos.pexels.com/video-files/3130284/3130284-uhd_2560_1440_30fps.mp4",
  "https://videos.pexels.com/video-files/2278095/2278095-hd_1920_1080_30fps.mp4",
];

// Course data
const COURSES = [
  {
    id: "master-ai-tools",
    title: "Master AI Tools",
    description: "Learn to leverage cutting-edge AI tools for productivity, automation, and creative work.",
    duration: "4 weeks",
    icon: Brain,
    color: "from-purple-500 to-indigo-600",
    features: [
      "ChatGPT, Claude & Gemini mastery",
      "Midjourney, DALL-E & Stable Diffusion",
      "Notion AI, Copilot & Jasper",
      "Runway, Synthesia & HeyGen (Video AI)",
      "Zapier AI & Make automation",
      "Canva AI, Adobe Firefly & Figma AI",
      "GitHub Copilot & Cursor IDE",
      "Real-world projects & certification"
    ]
  },
  {
    id: "python-data-science",
    title: "Python & Data Science",
    description: "Comprehensive Python programming with data science, machine learning, and analytics.",
    duration: "16 weeks",
    icon: Code,
    color: "from-blue-500 to-cyan-600",
    features: ["Python fundamentals", "Pandas & NumPy", "Machine Learning", "Real-world projects", "Certificate"]
  },
  {
    id: "cloud-computing",
    title: "Cloud Computing",
    description: "Master AWS, Azure, and GCP cloud platforms for modern infrastructure management.",
    duration: "10 weeks",
    icon: Cloud,
    color: "from-sky-500 to-blue-600",
    features: ["AWS certification prep", "Azure fundamentals", "DevOps practices", "Cloud architecture", "Certificate"]
  },
  {
    id: "cyber-security",
    title: "Cyber Security",
    description: "Learn ethical hacking, penetration testing, and security best practices.",
    duration: "12 weeks",
    icon: Shield,
    color: "from-red-500 to-orange-600",
    features: ["Ethical hacking", "Network security", "Penetration testing", "Security audits", "Certificate"]
  },
  {
    id: "digital-marketing",
    title: "Digital Marketing",
    description: "Complete digital marketing mastery including SEO, social media, and paid advertising.",
    duration: "8 weeks",
    icon: Megaphone,
    color: "from-pink-500 to-rose-600",
    features: ["SEO & SEM", "Social media marketing", "Google Ads", "Analytics & reporting", "Certificate"]
  },
  {
    id: "hardware-networking",
    title: "Hardware & Networking",
    description: "Complete hardware troubleshooting and network administration with assured placement.",
    duration: "6 weeks",
    icon: Server,
    color: "from-emerald-500 to-green-600",
    features: ["Hardware repair", "Network configuration", "Server management", "Assured Placement", "Certificate"],
    badge: "Assured Placement"
  }
];

const Navbar = ({ scrollToSection, onHiringClick, onViewJobs, onITStaffing, onBlog }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { name: "Home", id: "home" },
    { name: "Solutions", id: "solutions" },
    { name: "Industries", id: "industries" },
    { name: "Why Us", id: "why-us" },
    { name: "Career", id: "career" },
    { name: "About", id: "about" },
    { name: "Contact", id: "contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <div className="flex items-center">
            <img 
              src="/logo.png" 
              alt="MC Talent Solutions Pvt. Ltd." 
              className="h-20 w-auto"
              data-testid="brand-logo"
            />
          </div>
          
          <div className="hidden lg:flex items-center gap-5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-sm"
                data-testid={`nav-${item.id}`}
              >
                {item.name}
              </button>
            ))}
            <button
              onClick={onITStaffing}
              className="text-orange-600 hover:text-orange-700 transition-colors font-semibold text-sm flex items-center gap-1 bg-orange-50 px-3 py-1.5 rounded-full"
              data-testid="nav-it-staffing"
            >
              <Users className="h-4 w-4" />
              IT Staffing
            </button>
            <button
              onClick={onBlog}
              className="text-gray-700 hover:text-orange-500 transition-colors font-medium text-sm"
              data-testid="nav-blog"
            >
              Blog
            </button>
            <button
              onClick={onViewJobs}
              className="text-green-600 hover:text-green-700 transition-colors font-semibold text-sm"
              data-testid="nav-view-jobs"
            >
              View Jobs
            </button>
            <Button 
              onClick={onHiringClick}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 text-sm"
              data-testid="nav-hire-talent"
            >
              Hire Talent
            </Button>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  scrollToSection(item.id);
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left py-2 text-gray-700 hover:text-orange-500"
                data-testid={`mobile-nav-${item.id}`}
              >
                {item.name}
              </button>
            ))}
            <button
              onClick={() => {
                onITStaffing();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 text-orange-600 font-semibold"
              data-testid="mobile-nav-itstaffing"
            >
              IT Staffing
            </button>
            <button
              onClick={() => {
                onBlog();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 text-gray-700"
              data-testid="mobile-nav-blog"
            >
              Blog
            </button>
            <button
              onClick={() => {
                onViewJobs();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 text-green-600 font-semibold"
              data-testid="mobile-nav-viewjobs"
            >
              View Jobs
            </button>
            <button
              onClick={() => {
                onHiringClick();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 text-orange-600 font-bold"
              data-testid="mobile-nav-hire-talent"
            >
              Hire Talent
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = ({ onHireTalent, onViewJobs }) => {
  const videoRef = useRef(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % HERO_VIDEOS.length);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          key={currentVideoIndex}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEOS[currentVideoIndex]} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-cyan-500/10" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-7">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full backdrop-blur-sm">
              <Briefcase className="h-4 w-4 text-orange-400" />
              <span className="text-orange-300 text-sm font-medium">B2B Manpower Outsourcing</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight" data-testid="hero-title">
              End-to-End{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-cyan-400">
                Talent Solutions
              </span>{" "}
              for Modern Enterprises
            </h1>
            
            <p className="text-lg text-gray-300 max-w-xl leading-relaxed" data-testid="hero-description">
              Scale your workforce with pre-screened professionals across IT, BFSI, 
              Telecom and beyond. Contract staffing, permanent hiring, RPO and 
              payroll outsourcing — all under one trusted partner.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={onHireTalent}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-orange-500/25 transition-all hover:scale-105"
                data-testid="hero-hire-talent-btn"
              >
                Hire Talent
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                onClick={onViewJobs}
                className="border-white/30 text-white bg-white/5 hover:bg-white/15 rounded-full px-8 py-6 text-lg backdrop-blur-sm"
                data-testid="hero-view-jobs-btn"
              >
                View Jobs
              </Button>
            </div>

            <div className="flex flex-wrap gap-8 pt-4">
              <div>
                <div className="text-3xl font-bold text-white">50+</div>
                <div className="text-gray-400 text-sm">Client Companies</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">10+ Yrs</div>
                <div className="text-gray-400 text-sm">Expertise in IT Staffing</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">48h</div>
                <div className="text-gray-400 text-sm">Avg. Time to Shortlist</div>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20 p-8 rounded-3xl">
              <CardContent className="p-0 space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white shadow-xl">
                    <Target className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Pre-Screened Talent Pool</h3>
                    <p className="text-gray-300 text-sm">Skill-verified & interview-ready</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-white shadow-xl">
                    <Zap className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Fast Turnaround</h3>
                    <p className="text-gray-300 text-sm">Shortlists within 48 hours</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white shadow-xl">
                    <Shield className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Compliance & Payroll</h3>
                    <p className="text-gray-300 text-sm">End-to-end workforce management</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white shadow-xl">
                    <TrendingUp className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Pan-India Reach</h3>
                    <p className="text-gray-300 text-sm">Pune, Gurugram, Mumbai, Bangalore & more</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

// Course Enquiry Modal Component
const EnrollmentModal = ({ course, isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Course Enquiry: ${course.title}\n\n${formData.message || "I am interested in this course. Please contact me with more details."}`
      });

      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
      
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);

    } catch (err) {
      console.error("Enquiry error:", err);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  if (!course) return null;

  const IconComponent = course.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${course.color} flex items-center justify-center text-white mb-4`}>
            <IconComponent className="h-8 w-8" />
          </div>
          <DialogTitle className="text-2xl">Enquire About {course.title}</DialogTitle>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-gray-600">
              <Clock className="h-4 w-4" /> {course.duration}
            </span>
          </div>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Thank You!</h3>
            <p className="text-gray-600 mt-2">We'll contact you shortly with course details.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="enrollment-form">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="enrollment-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="enrollment-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="enrollment-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Any specific questions about this course?"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3}
                data-testid="enrollment-message"
              />
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                className={`w-full bg-gradient-to-r ${course.color} hover:opacity-90 text-white py-6 text-lg rounded-full`}
                disabled={loading}
                data-testid="enrollment-submit"
              >
                {loading ? "Submitting..." : "Submit Enquiry"}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Our team will contact you within 24 hours
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Hiring/JD Collection Modal Component
const HiringModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    jobTitle: "",
    jobDescription: "",
    experience: "",
    location: "",
    salaryRange: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/jobs`, {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        job_title: formData.jobTitle,
        job_description: formData.jobDescription,
        experience: formData.experience,
        location: formData.location,
        salary_range: formData.salaryRange
      });

      setSuccess(true);
      setFormData({
        companyName: "",
        contactPerson: "",
        email: "",
        phone: "",
        jobTitle: "",
        jobDescription: "",
        experience: "",
        location: "",
        salaryRange: ""
      });

      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2500);

    } catch (err) {
      console.error("Hiring submission error:", err);
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-4">
            <Briefcase className="h-8 w-8" />
          </div>
          <DialogTitle className="text-2xl">Submit Job Requirement</DialogTitle>
          <DialogDescription>
            Share your hiring needs and we'll find the perfect candidates for you.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Thank You!</h3>
            <p className="text-gray-600 mt-2">We've received your job requirement. Our team will contact you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="hiring-form">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  placeholder="Enter company name"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                  data-testid="hiring-company"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contact Person *</Label>
                <Input
                  id="contactPerson"
                  placeholder="Your name"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  required
                  data-testid="hiring-contact"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hiringEmail">Email *</Label>
                <Input
                  id="hiringEmail"
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="hiring-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hiringPhone">Phone *</Label>
                <Input
                  id="hiringPhone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="hiring-phone"
                />
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-gray-900 mb-3">Job Details</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle">Job Title *</Label>
                <Input
                  id="jobTitle"
                  placeholder="e.g., Software Developer"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  required
                  data-testid="hiring-jobtitle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  placeholder="e.g., Pune, Remote"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="hiring-location"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="experience">Experience Required</Label>
                <Input
                  id="experience"
                  placeholder="e.g., 2-4 years"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  data-testid="hiring-experience"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salaryRange">Salary Range</Label>
                <Input
                  id="salaryRange"
                  placeholder="e.g., 5-8 LPA"
                  value={formData.salaryRange}
                  onChange={(e) => setFormData({ ...formData, salaryRange: e.target.value })}
                  data-testid="hiring-salary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobDescription">Job Description *</Label>
              <Textarea
                id="jobDescription"
                placeholder="Describe the role, responsibilities, required skills, qualifications..."
                value={formData.jobDescription}
                onChange={(e) => setFormData({ ...formData, jobDescription: e.target.value })}
                rows={4}
                required
                data-testid="hiring-jd"
              />
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 text-white py-6 text-lg rounded-full"
                disabled={loading}
                data-testid="hiring-submit"
              >
                {loading ? "Submitting..." : "Submit Job Requirement"}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Our recruitment team will review and get back to you within 24 hours
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CoursesSection = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEnroll = (course) => {
    setSelectedCourse(course);
    setIsModalOpen(true);
  };

  return (
    <section id="courses" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="courses-title">
            Our <span className="text-orange-500">Courses</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Industry-relevant programs designed to accelerate your career growth
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {COURSES.map((course, index) => {
            const IconComponent = course.icon;
            return (
              <Card 
                key={course.id} 
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 overflow-hidden bg-white"
                data-testid={`course-card-${index}`}
              >
                <CardContent className="p-6">
                  {course.badge && (
                    <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {course.badge}
                    </div>
                  )}
                  
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${course.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="h-8 w-8" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{course.description}</p>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <Clock className="h-4 w-4" /> {course.duration}
                    </span>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {course.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="border-t pt-4">
                    <Button 
                      onClick={() => handleEnroll(course)}
                      className={`w-full bg-gradient-to-r ${course.color} hover:opacity-90 text-white rounded-full py-5`}
                      data-testid={`enroll-btn-${course.id}`}
                    >
                      Enquire Now
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <EnrollmentModal 
        course={selectedCourse}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCourse(null);
        }}
      />
    </section>
  );
};

const ClientsStrip = () => {
  // Each item is either { type: "img", src, name } or { type: "text", name, accent }
  const clients = [
    // Newly added (placed first so they're immediately visible)
    { type: "img", name: "ActioHX", src: "/clients/actiohx.jpeg" },
    { type: "img", name: "TCS", src: "/clients/tcs.webp" },
    { type: "img", name: "Tata Motors", src: "/clients/tata-motors.webp" },
    { type: "img", name: "Sony", src: "/clients/sony.webp" },
    { type: "img", name: "Digitide", src: "/clients/digitide.png" },
    { type: "img", name: "Precision Group", src: "/clients/precision-group.png" },
    { type: "img", name: "Extrieve", src: "/clients/extrieve.jpeg" },
    { type: "img", name: "Team Computers", src: "/clients/team-computers.webp" },
    { type: "img", name: "Sysnet Global", src: "/clients/sysnet.jpeg" },
    { type: "img", name: "CMS IT Services", src: "/clients/cms.png" },
    // Existing image logos
    { type: "img", name: "Accenture", src: "/clients/accenture.png" },
    { type: "img", name: "Airtel", src: "/clients/airtel.png" },
    { type: "img", name: "Axis Bank", src: "/clients/axis-bank.png" },
    { type: "img", name: "Birlasoft", src: "/clients/birlasoft.png" },
    { type: "img", name: "Genpact", src: "/clients/genpact.png" },
    { type: "img", name: "Reliance", src: "/clients/reliance.png" },
    { type: "img", name: "AON", src: "/clients/aon.png" },
    { type: "img", name: "Cognizant", src: "/clients/cognizant.png" },
    { type: "img", name: "IBM", src: "/clients/ibm.png" },
    { type: "img", name: "IndiaMART", src: "/clients/indiamart.png" },
    { type: "img", name: "Innodata", src: "/clients/innodata.png" },
    { type: "img", name: "Mphasis", src: "/clients/mphasis.png" },
    { type: "img", name: "Sahara", src: "/clients/sahara.png" },
    { type: "img", name: "Snapdeal", src: "/clients/snapdeal.png" },
    { type: "img", name: "Sopra", src: "/clients/sopra.png" },
    { type: "img", name: "Sun Life Financial", src: "/clients/sun-life.png" },
    { type: "img", name: "HAL", src: "/clients/hal.png" },
    { type: "img", name: "Hero", src: "/clients/hero.png" },
    { type: "img", name: "Honeywell", src: "/clients/honeywell.png" },
    { type: "img", name: "L&T", src: "/clients/lt.png" },
    { type: "img", name: "Reliance Life Sciences", src: "/clients/reliance-life-science.png" },
    { type: "img", name: "ING", src: "/clients/ing.png" },
    { type: "img", name: "Kotak", src: "/clients/kotak.png" },
    { type: "img", name: "Naukri", src: "/clients/naukri.png" },
    // Wordmark clients (awaiting official logo uploads)
    { type: "text", name: "Allied Digital", accent: "from-blue-700 to-cyan-600" },
    { type: "text", name: "3i Infotech", accent: "from-orange-600 to-amber-600" },
    { type: "text", name: "Teamlease", accent: "from-emerald-600 to-green-700" },
    { type: "text", name: "Dixit India", accent: "from-yellow-600 to-orange-600" },
    { type: "text", name: "Renovision", accent: "from-cyan-600 to-blue-600" },
    { type: "text", name: "Dnyacons", accent: "from-indigo-600 to-purple-600" },
    { type: "text", name: "Outworx", accent: "from-pink-600 to-rose-600" },
    { type: "text", name: "Net Lab India", accent: "from-teal-600 to-emerald-600" },
    { type: "text", name: "ITcons", accent: "from-blue-600 to-sky-500" },
    { type: "text", name: "Think App Solutions", accent: "from-violet-600 to-purple-700" },
    { type: "text", name: "PCS Technologies", accent: "from-orange-700 to-red-700" },
    { type: "text", name: "NTT Data Services", accent: "from-blue-700 to-blue-900" },
  ];

  // Duplicate the list for seamless infinite scroll
  const marquee = [...clients, ...clients];

  return (
    <section
      className="py-12 bg-white border-y border-gray-100 overflow-hidden"
      data-testid="clients-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
            <Award className="h-4 w-4 text-orange-600" />
            <span className="text-orange-700 text-sm font-semibold tracking-wide uppercase">Trusted Partners</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
            Few of Our <span className="text-orange-500">Recruiters</span>
          </h2>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="flex items-center gap-14 animate-marquee whitespace-nowrap">
          {marquee.map((client, i) =>
            client.type === "img" ? (
              <img
                key={i}
                src={client.src}
                alt={client.name}
                className="h-12 md:h-14 w-auto object-contain shrink-0"
                loading="lazy"
                data-testid={`client-logo-${i}`}
              />
            ) : (
              <span
                key={i}
                className={`text-2xl md:text-3xl font-extrabold tracking-tight bg-gradient-to-r ${client.accent} bg-clip-text text-transparent shrink-0`}
                data-testid={`client-logo-${i}`}
              >
                {client.name}
              </span>
            )
          )}
        </div>
      </div>
    </section>
  );
};

const SolutionsSection = ({ onHireTalent }) => {
  const solutions = [
    {
      icon: Users,
      title: "Contract Staffing",
      description: "Flexible short & long-term contract resources deployed on your projects within days.",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: Briefcase,
      title: "Permanent Hiring",
      description: "Full-time professionals sourced, screened and matched to your roles and culture.",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Target,
      title: "Contract-to-Hire",
      description: "Evaluate performance on the job before converting talent to permanent roles.",
      color: "from-emerald-500 to-green-600"
    },
    {
      icon: TrendingUp,
      title: "RPO Services",
      description: "End-to-end Recruitment Process Outsourcing — dedicated recruiters embedded with your team.",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: Shield,
      title: "Payroll Outsourcing",
      description: "Compliance, statutory filings and monthly payroll handled accurately and on time.",
      color: "from-rose-500 to-pink-600"
    },
    {
      icon: Award,
      title: "Executive Search",
      description: "Confidential search for leadership, CXO and niche senior positions across industries.",
      color: "from-amber-500 to-orange-600"
    },
  ];

  return (
    <section id="solutions" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-orange-600" />
            <span className="text-orange-700 text-sm font-semibold">What We Do</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="solutions-title">
            End-to-End <span className="text-orange-500">Talent Solutions</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            From a single hire to scaling entire teams — we handle sourcing, screening, compliance and onboarding.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <Card
                key={index}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white overflow-hidden"
                data-testid={`solution-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${solution.color} flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{solution.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={onHireTalent}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg"
            data-testid="solutions-cta-btn"
          >
            Discuss Your Requirement
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const IndustriesSection = () => {
  const industries = [
    { name: "IT & ITES", icon: Code },
    { name: "BFSI", icon: TrendingUp },
    { name: "Telecom", icon: Cpu },
    { name: "E-commerce & Retail", icon: Briefcase },
    { name: "Manufacturing", icon: Server },
    { name: "Education & EdTech", icon: GraduationCap },
    { name: "BPO / KPO", icon: Users },
    { name: "Automobile / EV Industry", icon: Zap },
  ];

  return (
    <section id="industries" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 rounded-full mb-4">
              <Target className="h-4 w-4 text-cyan-600" />
              <span className="text-cyan-700 text-sm font-semibold">Industries</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6" data-testid="industries-title">
              Deep Expertise Across <span className="text-orange-500">Industries</span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              Our recruitment specialists understand the nuances of each vertical —
              from niche tech skills to regulated domains like banking and telecom.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-700">
                <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Dedicated account managers per industry vertical</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700">
                <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Pre-built talent pipelines for faster delivery</span>
              </li>
              <li className="flex items-start gap-3 text-gray-700">
                <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span>Domain-trained recruiters for technical roles</span>
              </li>
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div
                  key={index}
                  className="group p-6 bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-2xl hover:border-orange-300 hover:shadow-lg transition-all"
                  data-testid={`industry-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center text-orange-500 mb-3 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="font-semibold text-gray-900">{industry.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyUsSection = () => {
  const reasons = [
    {
      icon: Users,
      title: "Pre-Screened Candidates",
      description: "Every candidate goes through multi-level screening — skill tests, communication checks and background verification."
    },
    {
      icon: Zap,
      title: "Speed to Hire",
      description: "Average shortlist within 48 hours. Critical roles filled in under a week with our active talent pool."
    },
    {
      icon: Shield,
      title: "Compliance First",
      description: "100% compliant with labour laws, PF/ESI, PT and statutory norms across all Indian states."
    },
    {
      icon: TrendingUp,
      title: "Proven Track Record",
      description: "A decade of IT staffing expertise across 50+ client companies, with 90%+ retention after 6 months."
    },
  ];

  return (
    <section id="why-us" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-cyan-500/5" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full mb-4">
            <Award className="h-4 w-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-semibold">Why MC Talent</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4" data-testid="why-us-title">
            Built for <span className="text-orange-400">Scale & Speed</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            A hiring partner that combines domain knowledge, tech-enabled sourcing and a genuine recruiter-first culture.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <Card
                key={index}
                className="bg-white/5 border-white/10 hover:bg-white/10 hover:border-orange-500/30 transition-all"
                data-testid={`why-us-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 flex items-center justify-center text-orange-400 mb-4">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{reason.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{reason.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    { number: "01", title: "Requirement Intake", description: "We deep-dive into your role, team culture and business goals." },
    { number: "02", title: "Talent Sourcing", description: "Active sourcing from our 1L+ candidate database & networks." },
    { number: "03", title: "Screening & Shortlist", description: "Skill tests, assessments and interviews before you see the profile." },
    { number: "04", title: "Onboard & Support", description: "Offer roll-out, joining, payroll and post-hire engagement." },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-orange-500">4-Step Process</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            A structured, transparent workflow that gets you the right hire — faster.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative p-8 bg-gradient-to-br from-orange-50 via-white to-cyan-50 border border-gray-100 rounded-2xl hover:shadow-xl transition-shadow"
              data-testid={`process-step-${index}`}
            >
              <div className="text-5xl font-bold text-orange-500/30 mb-4">{step.number}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 -right-3 text-orange-300">
                  <ChevronRight className="h-6 w-6" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const services = [
    {
      icon: <Users className="h-10 w-10" />,
      title: "Campus Placements",
      description: "End-to-end placement assistance for educational institutions with industry connections.",
    },
    {
      icon: <TrendingUp className="h-10 w-10" />,
      title: "Corporate Training",
      description: "Customized training programs for organizations to upskill their workforce.",
    },
    {
      icon: <Award className="h-10 w-10" />,
      title: "Certification Programs",
      description: "Industry-recognized certifications to validate your skills and expertise.",
    },
  ];

  return (
    <section id="services" className="py-24 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="services-title">
            Our <span className="text-orange-400">Services</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Comprehensive solutions for individuals and institutions
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="bg-white/5 border-white/10 hover:bg-white/10 transition-all group"
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-orange-500/20 to-orange-500/5 flex items-center justify-center text-orange-400 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-gray-400">{service.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const CareerApplyModal = ({ role, isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", resume: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const summary = `Role: ${role.title}\nExperience: ${role.experience}\nPackage: ${role.package}\nLocation: ${role.location}`;
      const fullMessage = `CAREER APPLICATION — ${role.title} (MC Talent Solutions — In-house)\n\n${summary}\n\nResume / LinkedIn: ${formData.resume || "Not provided"}\n\nMessage from candidate:\n${formData.message || "(none)"}`;

      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: fullMessage,
      });

      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", resume: "", message: "" });
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  if (!role) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-3">
            <Briefcase className="h-7 w-7" />
          </div>
          <DialogTitle className="text-2xl">Apply for {role.title}</DialogTitle>
          <DialogDescription>
            MC Talent Solutions — {role.location} · {role.experience}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8" data-testid="career-apply-success">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">Application Received!</h3>
            <p className="text-gray-600 mt-2">Our team will review your profile and get back within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="career-apply-form">
            {error && <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <div className="space-y-2">
              <Label htmlFor="career-name">Full Name *</Label>
              <Input id="career-name" placeholder="Enter your full name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="career-name" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="career-email">Email *</Label>
                <Input id="career-email" type="email" placeholder="you@email.com" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="career-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="career-phone">Phone *</Label>
                <Input id="career-phone" placeholder="+91 9XXXXXXXXX" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="career-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="career-resume">Resume / LinkedIn / Google Drive link</Label>
              <Input id="career-resume" placeholder="https://..." value={formData.resume}
                onChange={(e) => setFormData({ ...formData, resume: e.target.value })} data-testid="career-resume" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="career-message">Cover message (optional)</Label>
              <Textarea id="career-message" placeholder="Why you&apos;re a great fit for this role"
                value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={3} data-testid="career-message" />
            </div>

            <Button type="submit" disabled={loading} data-testid="career-submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:opacity-90 text-white py-6 text-lg rounded-full">
              {loading ? "Submitting..." : "Submit Application"}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              We&apos;ll contact shortlisted candidates within 24 hours.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CareerSection = () => {
  const roles = [
    {
      key: "it-recruiter",
      title: "IT Recruiter",
      eligibility: "MBA (HR) preferred",
      experience: "0 – 4 Years",
      package: "Up to 6 LPA",
      location: "Hybrid — Pune & Gurugram",
      joining: "Immediate",
      highlights: [
        "End-to-end IT recruitment — sourcing, screening, scheduling, closures",
        "Build & manage talent pipelines across tech stacks",
        "Client coordination and candidate relationship management",
        "Strong communication skills and a recruiter-first mindset",
      ],
    },
    {
      key: "bdm",
      title: "Business Development Manager",
      eligibility: "MBA preferred · Sales background in staffing / recruitment",
      experience: "Sales Profile",
      package: "Up to 10 LPA",
      location: "Hybrid",
      joining: "30 Days Notice — Acceptable",
      highlights: [
        "KRA: Onboard new clients and ensure smooth coordination throughout the process",
        "Build & nurture a strong B2B sales pipeline across industries",
        "Drive revenue growth, account expansion and strategic partnerships",
        "Reports directly to the Founder",
      ],
    },
  ];

  const [selected, setSelected] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openApply = (role) => {
    setSelected(role);
    setIsOpen(true);
  };

  const closeApply = () => {
    setIsOpen(false);
    setSelected(null);
  };

  return (
    <section id="career" className="py-24 bg-gradient-to-br from-white via-orange-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
            <Sparkles className="h-4 w-4 text-orange-600" />
            <span className="text-orange-700 text-sm font-semibold tracking-wide uppercase">Join Our Team</span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" data-testid="career-title">
            <span className="text-orange-500">Career</span> at MC Talent Solutions
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            We&apos;re growing fast. If you love people, process and problem-solving — we&apos;d love to meet you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {roles.map((role) => (
            <Card
              key={role.key}
              className="group relative overflow-hidden border-0 shadow-md hover:shadow-2xl transition-all"
              data-testid={`career-card-${role.key}`}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-orange-600" />
              <CardContent className="p-8">
                <div className="flex items-start justify-between mb-5 flex-wrap gap-2">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{role.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{role.eligibility}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold">
                    <Clock className="h-3 w-3" />
                    {role.joining}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Briefcase className="h-4 w-4 text-orange-500" />
                    <span>{role.experience}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <IndianRupee className="h-4 w-4 text-orange-500" />
                    <span>{role.package}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    <span className="truncate" title={role.location}>{role.location}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {role.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => openApply(role)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-5"
                  data-testid={`career-apply-btn-${role.key}`}
                >
                  Apply Now
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <CareerApplyModal role={selected} isOpen={isOpen} onClose={closeApply} />
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-testid="about-title">
              About <span className="text-orange-500">MC Talent Solutions</span>
            </h2>
            <div className="space-y-4 text-gray-600">
              <p>
                Founded by Monika Choudhary, MC Talent Solutions is a trusted manpower
                outsourcing and staffing partner — bridging the gap between enterprises
                and the right talent across India.
              </p>
              <p>
                With years of experience in HR, recruitment and workforce management,
                we run a tech-enabled sourcing engine backed by specialists across
                IT, BFSI, Telecom, Manufacturing and more.
              </p>
              <p>
                From contract staffing and permanent hiring to RPO and payroll
                outsourcing — we help enterprises scale their workforce faster,
                compliantly and with zero operational overhead.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="p-4 bg-orange-50 rounded-xl">
                <Briefcase className="h-8 w-8 text-orange-500 mb-2" />
                <div className="text-2xl font-bold text-gray-900">10+ Yrs</div>
                <div className="text-gray-600 text-sm">Expertise in IT Staffing</div>
              </div>
              <div className="p-4 bg-orange-50 rounded-xl">
                <Users className="h-8 w-8 text-orange-500 mb-2" />
                <div className="text-2xl font-bold text-gray-900">50+</div>
                <div className="text-gray-600 text-sm">Client Companies</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-orange-100 to-orange-50 p-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-40 h-40 mx-auto rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-6xl font-bold shadow-2xl mb-6">
                  MC
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Monika Choudhary</h3>
                <p className="text-orange-600 font-medium">Founder & Career Mentor</p>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-200 rounded-full opacity-50" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-orange-300 rounded-full opacity-50" />
          </div>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const [showAll, setShowAll] = useState(false);
  
  const testimonials = [
    {
      name: "Qusai Mustufa",
      role: "Placed at ActioHX as TSE 2",
      content: "Huge thanks to MC Talent Solutions for helping me secure the TSE 2 role at ActioHX. The team's guidance through every interview round, prompt updates and genuine support made the entire process effortless. Their professional approach truly stands out — highly recommended!",
      rating: 5
    },
    {
      name: "Satyam",
      role: "Selected at TCS, Pune",
      content: "I'm thankful to MC Talent Solutions for guiding me through the entire selection process at TCS, Pune. Right from the initial screening to the final round, the team was extremely supportive and professional. Their interview prep and timely follow-ups made a huge difference. Truly recommend them to anyone looking for the right opportunity.",
      rating: 5
    },
    {
      name: "Himanshu Singh",
      role: "Placed at Tata Motors, Lucknow",
      content: "MC Talent Solutions made my dream of working with Tata Motors come true. Right from profile shortlisting to final interview rounds, the team guided me at every step. The process was smooth, transparent and professional. Grateful to the entire team for believing in me!",
      rating: 5
    },
    {
      name: "Humanshu",
      role: "Placed at Tata Motors, Chandigarh",
      content: "I got placed at Tata Motors, Chandigarh through MC Talent Solutions and it has been a life-changing experience. Their personalised approach, mock interviews and constant follow-ups truly set them apart. A genuine recruitment partner who cares about candidates. Highly recommended!",
      rating: 5
    },
    {
      name: "Shweta Maurya",
      role: "Placed at Accenture",
      content: "MC Talent Hunt exceeded my expectations, securing my dream job. Their personalized approach, expert guidance, and dedication make them the top choice for anyone seeking career advancement. Highly recommended!",
      rating: 5
    },
    {
      name: "Nikita Yadav",
      role: "Placed at Accenture",
      content: "MC Talent Hunt did an amazing job helping me find the ideal job. They were really good at understanding what I needed and quickly offered me the right match. Their friendly and efficient communication made the whole process easy and stress-free.",
      rating: 5
    },
    {
      name: "Aman Raj",
      role: "Placed at ActioHX Pune",
      content: "Completing my IT training with them has been life changing. The entire team has been exceptionally supportive, guiding me every step of the way. Their training program is truly remarkable. I wholeheartedly recommend MC Talent Hunt!",
      rating: 5
    },
    {
      name: "Divya Kamble",
      role: "Placed at JIO Digital, Navi Mumbai",
      content: "I would like to thank MC Talent Hunt for their support. During technical training, some things were difficult for me, but they were always there to help. I got a job right after the completion of my training. My family and I are very much happy!",
      rating: 5
    },
    {
      name: "Mukesh Panday",
      role: "Placed at WNS Global Services, Gurugram",
      content: "Monika Ma'am is a true mentor. With her guidance and mentoring, I am placed at MNC today. The entire training curriculum was so helpful and designed as per the company's requirements. The grooming sessions were outstanding!",
      rating: 5
    },
    {
      name: "Jatin Saini",
      role: "Placed at Accenture, Gurugram",
      content: "MC Talent Hunt is one of the best HR Company in India. She was quick to understand the kind of role I was looking for, recognizing my strengths and putting me forward for exactly the right job. I could not recommend them more highly!",
      rating: 5
    },
    {
      name: "Harish Singhal",
      role: "Placed at Accenture, Gurugram",
      content: "Extremely satisfied with the way they brought about my process. Entire team at MC Talent Hunt is super supportive! I got placed with MNC at my desired location and profile. Many thanks!",
      rating: 5
    },
    {
      name: "Gopal",
      role: "Placed at Cognizant, Pune",
      content: "It was great pleasure to get in touch with MC Talent Hunt. They helped me a lot with getting placed & smooth onboarding. I especially thank Monika mam & her team for their constant support and great coordination. Outstanding job!",
      rating: 5
    },
    {
      name: "Ajay Yadav",
      role: "Placed at Cognizant, Pune",
      content: "I really thank MC Talent Hunt for my selection with top MNC, Cognizant. Team is really very supportive and encouraging. I got placed just within 2 days of sharing my profile. Highly recommended to all hunting for job in IT sector!",
      rating: 5
    },
    {
      name: "Priya Umate",
      role: "Placed at FIS Global, Mumbai",
      content: "Thank you very much for this wonderful opportunity. I got my first job today. MC Talent Hunt really takes lots of efforts in finding the suitable job role. As a fresher, this platform is very good for learning and building career. Highly recommended!",
      rating: 5
    },
    {
      name: "Kamal Kanti",
      role: "Placed at IBM, Pune",
      content: "What a wonderful company! I am selected in big MNC with all efforts taken by MC Talent Hunt. Highly recommended to all those who are willing to build their career in IT. Thank you!",
      rating: 5
    },
    {
      name: "Sapna Chapre",
      role: "Placed at IBM, Mumbai",
      content: "MC Talent Hunt is awesome. Team is very helpful. They co-ordinate with the job applicants very well. If there is any issue, they resolve it perfectly just in no time. I am very thankful to them for providing me the right job opportunity.",
      rating: 5
    },
    {
      name: "Rohan Joshi",
      role: "Placed at Accenture, Bangalore",
      content: "My experience with MC Talent Hunt was the best in terms of education and placement. Monica Ma'am assisted me in honing my soft skills. I highly recommend MCTH to anyone in need of technical training and placement.",
      rating: 5
    },
    {
      name: "Deepak Pandey",
      role: "Placed at Wipro, Pune",
      content: "My experience with MC Talent Hunt was one that I will never forget. Their immense support and guidance helped me achieve my goal. I can vouch for their services to be the very best as they have been fully supportive of me.",
      rating: 5
    },
    {
      name: "Shivani Dange",
      role: "Placed at Reliance Jio",
      content: "They not just helped me in raising my confidence but also supported throughout the process. All my doubts were cleared before taking up the interview call. I do recommend this company to all who are in search of good placements in IT.",
      rating: 5
    },
    {
      name: "Amit Singh Chauhan",
      role: "Placed at IBM, Gurugram",
      content: "MC Talent Hunt provided me job within 10 days in my 2nd interview which is commendable. They helped me a lot in every step. Get in touch with this company. They will genuinely support you.",
      rating: 5
    },
    {
      name: "Manisha Kadam",
      role: "Placed at ActioHX, Pune",
      content: "Monika Mam's soft skills sessions are amazing! She helps in building up every student's confidence level. I am much happy today that I got placed at one of MNC's in Pune. Even my parents are happier than me!",
      rating: 5
    },
    {
      name: "Sneha Savekar",
      role: "Placed at Concentrix",
      content: "It was a great experience to get in touch with the company. I truly enjoyed the process followed by the placement team. Their support for me getting placed at Pune location is a great thing. I will recommend joining MC Talent Hunt!",
      rating: 5
    },
    {
      name: "Diksha Bhutekar",
      role: "Placed at Nimap Infotech, Mumbai",
      content: "I am extremely grateful to MC Talent Hunt for helping me secure a position as an Associate Developer. The team's personalized attention and resources were instrumental in my success. Thank you for helping job seekers find meaningful employment!",
      rating: 5
    },
    {
      name: "Naresh Sananse",
      role: "Placed at ActioHX Pune",
      content: "I am immensely grateful for the support that led to my successful campus placement through MC Talent Hunt. The streamlined process enabled me to secure placement in just a remarkable two-day timeframe. Kudos to the entire team!",
      rating: 5
    },
    {
      name: "Rahul Meshram",
      role: "Placed at ActioHX, Pune",
      content: "I would like to thank Siddharth Sir, Monika Ma'am, and MC Talent Hunt for their consistent support in learning hardware and networking skills along with interview skills. Best platform for young graduates who want to build their career in IT Sector!",
      rating: 5
    },
    {
      name: "Vaibhav",
      role: "Placed at Cognizant, Pune",
      content: "MC Talent Hunt is one of the best recruiters I have ever encountered. The interviewing process and handling the job seeker is at good quality level. They followed up my case very fast. Best recommendation for candidates who want better opportunity!",
      rating: 5
    },
    {
      name: "Sumit Wankhede",
      role: "Placed at WNS Global Services, Pune",
      content: "I got placed in WNS IT services, Pune. I had a great time and learned a lot from the technical classes. Thank you so much Monika Ma'am and MC Talent Hunt for helping me to get my first job in IT sector.",
      rating: 5
    },
    {
      name: "Avinash Kumar",
      role: "Placed at Accenture, Gurugram",
      content: "It was a pleasure to get in touch with MC Talent Hunt. They help a lot for a successful career ranging from technical training to final placement. The entire team is outstanding with communication. Thanks for the great opportunity!",
      rating: 5
    },
  ];

  const displayedTestimonials = showAll ? testimonials : testimonials.slice(0, 6);

  return (
    <section id="testimonials" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="testimonials-title">
            Client & Placement <span className="text-orange-500">Success Stories</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real stories from professionals we've placed and enterprises we've scaled.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedTestimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow"
              data-testid={`testimonial-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {testimonials.length > 6 && (
          <div className="text-center mt-12">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outline"
              className="border-orange-500 text-orange-500 hover:bg-orange-50 rounded-full px-8 py-6"
              data-testid="show-more-testimonials"
            >
              {showAll ? "Show Less" : `Show All ${testimonials.length} Testimonials`}
              <ChevronRight className={`ml-2 h-4 w-4 transition-transform ${showAll ? "rotate-90" : ""}`} />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      alert("Thank you for your inquiry! We will get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      alert("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-testid="contact-title">
              Get in <span className="text-orange-500">Touch</span>
            </h2>
            <p className="text-gray-600 mb-8">
              Looking to hire or scale your workforce? Share your requirement and our team will get back within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Phone</div>
                  <div className="text-gray-600">+91 93503 64233</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Email</div>
                  <div className="text-gray-600">hiring@mctalentsolutionsai.com</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-orange-500" />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="font-medium text-gray-900">Gurugram Office</div>
                    <div className="text-gray-600 text-sm">10th Floor, Tower-B, Unitech Cyber Park,</div>
                    <div className="text-gray-600 text-sm">Sector 39, Gurugram, Haryana 122001, India</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Pune Office</div>
                    <div className="text-gray-600 text-sm">Regus Co-Working, 4th Floor, Prabhavee Tech Park,</div>
                    <div className="text-gray-600 text-sm">Baner, Pune, Maharashtra 411045, India</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Input
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-12"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={4}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white h-12 rounded-full"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="mb-4">
              <img 
                src="/logo.png" 
                alt="MC Talent Solutions Pvt. Ltd." 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm">
              Enrich • Execute • Evolve
            </p>
            <p className="text-gray-400 text-sm mt-2">
              End-to-end manpower outsourcing and staffing partner for modern enterprises.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#home" className="hover:text-orange-400 transition-colors">Home</a></li>
              <li><a href="#solutions" className="hover:text-orange-400 transition-colors">Solutions</a></li>
              <li><a href="#industries" className="hover:text-orange-400 transition-colors">Industries</a></li>
              <li><a href="#why-us" className="hover:text-orange-400 transition-colors">Why Us</a></li>
              <li><a href="/blog" className="hover:text-orange-400 transition-colors">Blog</a></li>
              <li><a href="#about" className="hover:text-orange-400 transition-colors">About Us</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Solutions</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>Contract Staffing</li>
              <li>Permanent Hiring</li>
              <li>RPO Services</li>
              <li>Payroll Outsourcing</li>
              <li>Executive Search</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li>+91 93503 64233</li>
              <li>hiring@mctalentsolutionsai.com</li>
              <li className="pt-2 text-gray-300 font-medium">Gurugram Office</li>
              <li>10th Floor, Tower-B, Unitech Cyber Park,</li>
              <li>Sector 39, Gurugram – 122001</li>
              <li className="pt-2 text-gray-300 font-medium">Pune Office</li>
              <li>Regus Co-Working, 4th Floor, Prabhavee Tech Park,</li>
              <li>Baner, Pune – 411045</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>&copy; 2026 MC Talent Solutions Pvt. Ltd. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

const Home = () => {
  const [isHiringModalOpen, setIsHiringModalOpen] = useState(false);
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 96;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth"
      });
    }
  };

  const navigate = useNavigate();
  const openHire = () => setIsHiringModalOpen(true);

  return (
    <div className="min-h-screen">
      <Navbar 
        scrollToSection={scrollToSection} 
        onHiringClick={openHire} 
        onViewJobs={() => navigate("/jobs")}
        onITStaffing={() => navigate("/it-staffing")}
        onBlog={() => navigate("/blog")}
      />
      <HeroSection onHireTalent={openHire} onViewJobs={() => navigate("/jobs")} />
      <ClientsStrip />
      <SolutionsSection onHireTalent={openHire} />
      <IndustriesSection />
      <WhyUsSection />
      <ProcessSection />
      <CareerSection />
      <AboutSection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
      <HiringModal isOpen={isHiringModalOpen} onClose={() => setIsHiringModalOpen(false)} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/it-staffing" element={<OutsourcingPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
